/**
 * Block Registry — maps semantic block types to React components.
 *
 * Keyed by UIBlockType (not tool name). Has no knowledge of
 * tools, streams, or agents.
 */

import { type ComponentType, lazy } from 'react';
import type { UIBlockType } from './blocks/types.js';
import { ActionList, ActionListSkeleton } from './components/ActionList.js';
import { ChartSkeleton } from './components/ChartSkeleton.js';
import { DataTable, TableSkeleton } from './components/DataTable.js';
import { EntityCard, EntityCardSkeleton } from './components/EntityCard.js';
import { KPICards, KPISkeleton } from './components/KPICards.js';
import { Question, QuestionSkeleton } from './components/Question.js';
import { Timeline, TimelineSkeleton } from './components/Timeline.js';

const LazyChart = lazy(() => import('./components/Chart.js').then((m) => ({ default: m.Chart })));

export interface BlockComponentProps {
  data: any;
  toolCallId?: string;
  preSelectedId?: string | null;
}

export interface BlockRegistryEntry {
  component: ComponentType<BlockComponentProps>;
  skeleton: ComponentType<{ partialData?: unknown }>;
  displayName: string;
  lazy?: boolean;
}

export const BLOCK_REGISTRY: Partial<Record<UIBlockType, BlockRegistryEntry>> = {
  table: {
    component: DataTable,
    skeleton: TableSkeleton,
    displayName: 'Loading table',
  },
  kpi: {
    component: KPICards,
    skeleton: KPISkeleton,
    displayName: 'Loading metrics',
  },
  entity_card: {
    component: EntityCard,
    skeleton: EntityCardSkeleton,
    displayName: 'Loading details',
  },
  chart: {
    component: LazyChart,
    skeleton: ChartSkeleton,
    displayName: 'Loading chart',
    lazy: true,
  },
  timeline: {
    component: Timeline,
    skeleton: TimelineSkeleton,
    displayName: 'Loading timeline',
  },
  action_list: {
    component: ActionList,
    skeleton: ActionListSkeleton,
    displayName: 'Loading actions',
  },
  question: {
    component: Question,
    skeleton: QuestionSkeleton,
    displayName: 'Loading options',
  },
};
